import {mutation, query} from "@/graphql/client";
import {
    AutopilotJobsite,
    AutopilotPlayState,
    AutopilotPreference,
    Jobsite,
    JobsiteEmployeeFlagClientPreventedFragment, JobsiteEmployeeFlagIsBookedFragment,
    JobsiteFragmentFragment, JobsiteWithEmployeeFlagsFragment,
    PlanningTemplate,
    WorktimeMode
} from "@/graphql/generated/graphql";
import {CalendarMonth, generateCalendarMonth, sorter as calendarMonthSorter} from "@/helper/calendar/calendarMonth";
import {useGlobalEmitter} from "@/helper/emitter";
import {graphql} from "@/graphql/generated";

export const autopilotPreferenceFragment = graphql(`
    fragment AutopilotPreferenceFragment on AutopilotPreference {
        month
        worktimeMode
        playState
        bookingTarget
        plannedAbsenceTarget
        autopilotJobsites {
            source
            jobsite {
                ...JobsiteFragment
                ...JobsiteEmployeeFlagIsBooked
            }
        }
    }`)

export class AutopilotPreferenceObject {
    constructor(
        public id: string,
        public month: CalendarMonth,
        public autopilotJobsites: Array<AutopilotJobsite>,
        public worktimeMode: WorktimeMode | undefined | null,
        public bookingTarget: number | undefined | null,
        public playState: AutopilotPlayState
    ) {}

    static createFromGraphQL(autopilotPreference: AutopilotPreference) : AutopilotPreferenceObject {
        return new AutopilotPreferenceObject(
            autopilotPreference.month,
            generateCalendarMonth(autopilotPreference.month),
            autopilotPreference.autopilotJobsites,
            autopilotPreference.worktimeMode,
            autopilotPreference.bookingTarget,
            autopilotPreference.playState
        )
    }
}

export async function fetchAutopilotPreferenceJobsites(month: CalendarMonth): Promise<JobsiteWithEmployeeFlagsFragment[] | undefined> {
    const result = await query(graphql(`
        query getAutopilotPreferenceJobsites(
            $month: KapitelDateImmutable!
        ) {
            autopilotPreference (month: $month){
                autopilotJobsites {
                    jobsite {
                        ...JobsiteWithEmployeeFlags
                    }
                }
            }
        }`), {
            month: month.begin
        }
    )

    return result?.data?.autopilotPreference?.autopilotJobsites
        ? result?.data?.autopilotPreference?.autopilotJobsites.map((autopilotPreference: {jobsite: JobsiteWithEmployeeFlagsFragment}) => (autopilotPreference.jobsite))
        : undefined
}


export async function fetchAutopilotPreferenceObject(month: CalendarMonth): Promise<AutopilotPreferenceObject | undefined> {
    const result = await query(graphql(`
        
        
        
        query getAutopilotPreference(
            $month: KapitelDateImmutable!
        ) {
            autopilotPreference (month: $month){
                ...AutopilotPreferenceFragment
            }
        }`), {
            month: month.begin
        }
    )

    const autopilotPreference = result?.data?.autopilotPreference

    return autopilotPreference
        ? AutopilotPreferenceObject.createFromGraphQL(autopilotPreference)
        : undefined
}

export async function updateAutopilotPreference(
    month: CalendarMonth,
    autopilotPreferenceProperties: {
        worktimeMode?: WorktimeMode
        playState?: AutopilotPlayState
        bookingTarget?: number,
        plannedAbsenceTarget?:number
    }
): Promise<AutopilotPreferenceObject> {

    const result = await mutation(graphql(`
        
        
        
        mutation updateAutopilotPreference(
            $month: KapitelDateImmutable!,
            $worktimeMode: WorktimeMode,
            $playState: AutopilotPlayState,
            $bookingTarget: Int,
            $plannedAbsenceTarget: Int
        ) {
            updateAutopilotPreference(
                month: $month
                worktimeMode: $worktimeMode
                playState: $playState
                bookingTarget: $bookingTarget
                plannedAbsenceTarget: $plannedAbsenceTarget
            ) {
                ...AutopilotPreferenceFragment
            }
        }`), {
            month: month.begin,
            worktimeMode: autopilotPreferenceProperties.worktimeMode || null,
            playState: autopilotPreferenceProperties.playState || null,
            bookingTarget: typeof autopilotPreferenceProperties.bookingTarget != 'undefined' ? autopilotPreferenceProperties.bookingTarget : null,
            plannedAbsenceTarget: typeof autopilotPreferenceProperties.plannedAbsenceTarget != 'undefined' ? autopilotPreferenceProperties.plannedAbsenceTarget : null,
        }
    )

    useGlobalEmitter().emit('autopilotPreferences:mutated', month.getId())

    return AutopilotPreferenceObject.createFromGraphQL(result.data.updateAutopilotPreference)
}

export async function addJobsiteToAutopilot(month: CalendarMonth, jobsite: Jobsite|JobsiteWithEmployeeFlagsFragment): Promise<AutopilotJobsite> {

    const result = await mutation(graphql(`
            mutation addJobsiteToAutopilot(
                $month: KapitelDateImmutable!,
                $jobsiteId: Int!
            ) {
                addJobsiteToAutopilot(
                    month: $month
                    jobsiteId: $jobsiteId
                ) {
                    jobsite {
                        id
                    }
                }
            }`), {
            month: month.begin,
            jobsiteId: jobsite.id
        }
    )

    useGlobalEmitter().emit('autopilotPreferences:mutated', month.getId())

    return result.data
}

export async function removeJobsiteFromAutopilot(month: CalendarMonth, jobsite: Jobsite|JobsiteWithEmployeeFlagsFragment): Promise<AutopilotJobsite> {

    const result = await mutation(graphql(`
            mutation removeJobsiteFromAutopilot(
                $month: KapitelDateImmutable!,
                $jobsiteId: Int!
            ) {
                removeJobsiteFromAutopilot(
                    month: $month
                    jobsiteId: $jobsiteId
                ) {
                    jobsite {
                        id
                    }
                }
            }`), {
            month: month.begin,
            jobsiteId: jobsite.id
        }
    )

    useGlobalEmitter().emit('autopilotPreferences:mutated', month.getId())

    return result.data
}

/**
 * to be used as compareFn for AutopilotPreferenceObject[].sort()
 */
export function sorter(a : AutopilotPreferenceObject, b : AutopilotPreferenceObject) {
    return calendarMonthSorter(a.month, b.month)
}

export function getWorktimeModes() {
    return [
        WorktimeMode.More,
        WorktimeMode.SpotOn,
        WorktimeMode.Less
    ]
}

export function getWorktimeMode(actual: number, spotOn: number) {
    if (actual == spotOn) return WorktimeMode.SpotOn
    else if (actual > spotOn) return WorktimeMode.More
    else if (actual < spotOn) return WorktimeMode.Less
}

export function getWorktimeModeLabel(worktimeMode: string) {
    switch(worktimeMode) {
        case WorktimeMode.Less: return 'Weniger arbeiten'
        case WorktimeMode.SpotOn: return 'Sollstunden'
        case WorktimeMode.More: return 'Überstunden ansammeln'
    }
}

export const getIconForWorktimeMode = (worktimeMode: string) => {
    switch (worktimeMode) {
        case WorktimeMode.Less: return '/icons/trend-down.svg'
        case WorktimeMode.SpotOn: return '/icons/target.svg'
        case WorktimeMode.More: return '/icons/trend-up.svg'
    }
}

export function getIconForPlayState(type: AutopilotPlayState | AutopilotPreference ) {
    switch (type) {
        case AutopilotPlayState.Running: return '/icons/play-circle.svg'
        case AutopilotPlayState.Paused: return '/icons/pause-circle.svg'
        case AutopilotPlayState.Scrapped: return '/icons/stop-circle.svg'
    }
}

export async function fetchPlanningTemplate(month: CalendarMonth): Promise<PlanningTemplate> {
    const result = await query(graphql(`
            query getPlanningTemplate(
                $month: KapitelDateImmutable!
            ) {
                planningTemplate (month: $month){
                    bookingTargetMax
                    bookingTargetMin
                    bookingTargetSpotOn
                    bookingTargetSuggestion
                    availabilityShiftSuggestion {
                        dow
                        shifts
                    }
                    absenceDayHoursFloat
                    averageShiftDuration
                }
            }`), {
            month: month.begin
        }
    )

    return result.data.planningTemplate
}

export async function fetchJobsiteSuggestions(month: CalendarMonth): Promise<Jobsite[]> {


    const result = await query(
        graphql(`
            
            
            query GetJobsiteSuggestions (
                $month: KapitelDateImmutable!
            ){
              jobsiteSuggestions (
                month: $month
              ){
                ...JobsiteFragment                    
                client {
                    ...ClientFragment
                }
                house {
                    ...HouseFragment
                }
              }
        }`),
        {
            month: month.begin
        }
    )

    return result?.data?.jobsiteSuggestions

}
